import React, { useState } from 'react';

export interface Prompt {
  id: string;
  question: string;
  category: string;
  difficulty: number;
  isPremium: boolean;
}

interface ConversationPromptProps {
  prompt: Prompt | null;
  onSkip?: () => void;
  onEngage?: () => void;
}

export const ConversationPrompt: React.FC<ConversationPromptProps> = ({
  prompt,
  onSkip,
  onEngage,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasEngaged, setHasEngaged] = useState(false);

  if (!prompt) {
    return null;
  }

  const handleSkip = () => {
    if (onSkip) {
      onSkip();
    }
  };

  const handleEngage = () => {
    if (!hasEngaged) {
      setHasEngaged(true);
      if (onEngage) {
        onEngage();
      }
    }
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Get difficulty color
  const getDifficultyColor = (difficulty: number): string => {
    if (difficulty <= 2) return '#22c55e'; // Green - Easy
    if (difficulty <= 3) return '#f59e0b'; // Orange - Medium
    return '#ef4444'; // Red - Hard
  };

  // Get category emoji
  const getCategoryEmoji = (category: string): string => {
    const emojiMap: { [key: string]: string } = {
      Gratitude: '🙏',
      Vulnerability: '💙',
      Dreams: '✨',
      Fears: '😰',
      Connection: '🤝',
      Growth: '🌱',
      Reflection: '🪞',
      Deep: '🌊',
      Identity: '🎭',
      Existential: '🌌',
    };
    return emojiMap[category] || '💬';
  };

  if (isMinimized) {
    return (
      <div style={styles.minimizedContainer} onClick={toggleMinimize}>
        <span style={styles.minimizedIcon}>💬</span>
        <span style={styles.minimizedText}>Conversation Prompt</span>
        <span style={styles.expandIcon}>▲</span>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <span style={styles.categoryEmoji}>{getCategoryEmoji(prompt.category)}</span>
          <span style={styles.category}>{prompt.category}</span>
          {prompt.isPremium && (
            <span style={styles.premiumBadge}>Premium</span>
          )}
        </div>
        <button onClick={toggleMinimize} style={styles.minimizeButton}>
          ▼
        </button>
      </div>

      {/* Question */}
      <div style={styles.questionContainer}>
        <p style={styles.question}>{prompt.question}</p>
      </div>

      {/* Difficulty Indicator */}
      <div style={styles.difficultyContainer}>
        <span style={styles.difficultyLabel}>Depth:</span>
        <div style={styles.difficultyDots}>
          {[1, 2, 3, 4, 5].map((level) => (
            <div
              key={level}
              style={{
                ...styles.difficultyDot,
                backgroundColor:
                  level <= prompt.difficulty
                    ? getDifficultyColor(prompt.difficulty)
                    : '#e5e7eb',
              }}
            />
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div style={styles.actions}>
        {!hasEngaged && (
          <button onClick={handleEngage} style={styles.engageButton}>
            💬 Discuss This
          </button>
        )}
        {hasEngaged && (
          <div style={styles.engagedText}>
            ✓ Thanks for engaging!
          </div>
        )}
        <button onClick={handleSkip} style={styles.skipButton}>
          Skip
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'absolute',
    top: '20px',
    right: '280px', // Position to the left of local video
    width: '320px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 'var(--radius-lg)',
    padding: '1.25rem',
    boxShadow: 'var(--shadow-lg)',
    zIndex: 100,
  },
  minimizedContainer: {
    position: 'absolute',
    top: '20px',
    right: '280px',
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: 'var(--radius-lg)',
    padding: '0.75rem 1rem',
    boxShadow: 'var(--shadow-md)',
    zIndex: 100,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  minimizedIcon: {
    fontSize: '1.25rem',
  },
  minimizedText: {
    fontSize: '0.9rem',
    fontWeight: '600',
    color: 'var(--color-dark-navy)',
  },
  expandIcon: {
    fontSize: '0.8rem',
    color: 'var(--color-gray-500)',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  categoryEmoji: {
    fontSize: '1.25rem',
  },
  category: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: 'var(--color-gray-600)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  premiumBadge: {
    backgroundColor: '#f59e0b',
    color: 'white',
    padding: '0.15rem 0.4rem',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.65rem',
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  minimizeButton: {
    background: 'transparent',
    border: 'none',
    fontSize: '0.9rem',
    color: 'var(--color-gray-500)',
    cursor: 'pointer',
    padding: '0.25rem',
  },
  questionContainer: {
    marginBottom: '1rem',
  },
  question: {
    fontSize: '1rem',
    fontWeight: '500',
    color: 'var(--color-dark-navy)',
    lineHeight: '1.6',
    margin: 0,
  },
  difficultyContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  difficultyLabel: {
    fontSize: '0.8rem',
    color: 'var(--color-gray-600)',
    fontWeight: '500',
  },
  difficultyDots: {
    display: 'flex',
    gap: '0.25rem',
  },
  difficultyDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  actions: {
    display: 'flex',
    gap: '0.75rem',
    alignItems: 'center',
  },
  engageButton: {
    flex: 1,
    padding: '0.65rem 1rem',
    backgroundColor: 'var(--color-warm-orange)',
    color: 'white',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  engagedText: {
    flex: 1,
    padding: '0.65rem 1rem',
    backgroundColor: '#dcfce7',
    color: '#16a34a',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.85rem',
    fontWeight: '600',
    textAlign: 'center',
  },
  skipButton: {
    padding: '0.65rem 1rem',
    backgroundColor: 'transparent',
    color: 'var(--color-gray-600)',
    border: '1px solid var(--color-gray-300)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.85rem',
    fontWeight: '500',
    cursor: 'pointer',
  },
};
